import theme from "@theme";
import { Icon } from "@thetreep/ui";
import React, { useState, useRef, useEffect, ReactElement } from "react";
import { useTranslation } from "react-i18next";

interface Option<T> {
  value: T;
  label: string;
}

interface BaseSelectProps<T> {
  options: Option<T>[];
  multiple?: boolean;
  placeholder?: string;
  className?: string;
  error?: boolean;
  errorMsg?: string;
  search?: boolean;
  searchPlaceholder?: string;
  label?: string;
  optional?: boolean;
  kind?: string;
}

interface RequiredSelectProps<T> extends BaseSelectProps<T> {
  optional?: false;
  value: T | T[];
  onChange: (value: T | T[]) => void;
}

interface OptionalSelectProps<T> extends BaseSelectProps<T> {
  optional: true;
  value?: T | T[];
  onChange: (value: T | T[] | undefined) => void;
}

type SelectProps<T> = RequiredSelectProps<T> | OptionalSelectProps<T>;

function Select<T>({
  options,
  multiple = false,
  value,
  placeholder,
  className = "",
  error = false,
  errorMsg = "",
  search,
  searchPlaceholder,
  label,
  optional,
  kind,
  onChange,
}: SelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder || t("selectPlaceholder");

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // permissive search
  const normalize = (str: string) =>
    str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  const normalizedSearch = normalize(searchTerm);

  const filteredOptions = options.filter((option) =>
    normalize(option.label).includes(normalizedSearch),
  );

  const selectedValues = new Set(multiple && value ? (value as T[]) : []);

  const selectedLabels = multiple
    ? options.filter((option) => selectedValues.has(option.value))
    : options.filter((option) => option.value === value);

  const handleOptionClick = (optionValue: T) => {
    if (multiple) {
      const currentValues = (value as T[]) || [];
      const newValue = currentValues.includes(optionValue)
        ? currentValues.filter((v) => v !== optionValue)
        : [...currentValues, optionValue];
      onChange(newValue);
    } else {
      if (optional && value === optionValue) {
        // if we click on the same option and the field is optional, we reset the value
        onChange(undefined as T);
      } else {
        onChange(optionValue);
      }
      setIsOpen(false);
    }
    setSearchTerm("");
  };

  const removeOption = (optionValue: T, e: React.MouseEvent) => {
    e.stopPropagation();
    if (multiple) {
      const currentValues = value as T[];
      onChange(currentValues.filter((v) => v !== optionValue));
    }
  };

  const getDisplayedValue = (): ReactElement | ReactElement[] => {
    if (selectedLabels.length === 0) {
      return (
        <span className="text-gray-500 text-sm px-2 py-1">
          {resolvedPlaceholder}
        </span>
      );
    } else if (multiple) {
      const MAX_VISIBLE = 2;
      const visible = selectedLabels.slice(0, MAX_VISIBLE);
      const hiddenCount = selectedLabels.length - visible.length;

      return (
        <div className="flex-1 flex flex-wrap gap-1 max-w-full ">
          {visible.map((option) => (
            <span
              key={option.label}
              className="bg-blue-100 text-cnam-blue rounded px-2 py-1 text-sm flex items-center gap-1"
              onClick={(e) => removeOption(option.value, e)}
            >
              {option.label}
              <Icon
                src="close_line"
                color={theme.colors.cnam.blue.DEFAULT}
                size={16}
              />
            </span>
          ))}
          {hiddenCount > 0 && (
            <span className="text-gray-500 text-sm px-2 py-1">
              +{hiddenCount}
            </span>
          )}
        </div>
      );
    } else {
      return selectedLabels.map((option) => (
        <span
          key={option.label}
          className="text-sm p-1 overflow-ellipsis overflow-hidden whitespace-nowrap"
        >
          {option.label}
        </span>
      ));
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {label && <span className="text-xs font-bold">{label}</span>}
      <div ref={containerRef} className={`relative ${className}`}>
        <div
          className={`min-h-10 flex justify-between items-center ${
            kind === "filter"
              ? "border border-[#c6c6c6] border-solid bg-white rounded-full px-3 py-2"
              : "border rounded-md bg-white px-2 py-3"
          } cursor-pointer relative ${isOpen ? "opacity-0" : "opacity-100"} transition-opacity duration-0`}
          onClick={() => setIsOpen(!isOpen)}
        >
          {getDisplayedValue()}
          <Icon
            src={isOpen ? "up_line" : "down_line"}
            color={theme.colors.gray[500]}
            size={14}
          />
        </div>

        {isOpen && (
          <div className="absolute top-0 left-0 right-0 z-30 bg-white border rounded-md shadow-lg">
            {search && (
              <div className="sticky top-0 bg-white border-b z-30">
                <div className="flex items-center gap-4 p-4 w-full text-gray-500 text-sm">
                  <Icon
                    src="search_line"
                    color={theme.colors.gray[500]}
                    size={14}
                  />
                  <input
                    type="text"
                    className="focus:outline-none flex-1"
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                  <Icon
                    src="up_line"
                    color={theme.colors.gray[500]}
                    size={14}
                  />
                </div>
              </div>
            )}
            {!search && (
              <div className="bg-white border-b rounded-t-md">
                <div className="flex items-center justify-between px-2 py-3">
                  <span className="text-sm">
                    {selectedLabels.length > 0
                      ? selectedLabels[0].label
                      : resolvedPlaceholder}
                  </span>
                  <Icon
                    src="up_line"
                    color={theme.colors.gray[500]}
                    size={14}
                  />
                </div>
              </div>
            )}
            <div className="max-h-60 overflow-auto">
              {filteredOptions
                .sort((a, b) => (a.label > b.label ? 1 : -1))
                .map((option) => (
                  <div
                    key={option.label}
                    className={`
                p-2 cursor-pointer flex items-center justify-between
                ${
                  multiple
                    ? selectedValues.has(option.value)
                      ? "bg-blue-50"
                      : "hover:bg-gray-100"
                    : option.value === value
                      ? "bg-blue-50"
                      : "hover:bg-gray-100"
                }
              `}
                    onClick={() => handleOptionClick(option.value)}
                  >
                    {option.label}
                    {(multiple && selectedValues.has(option.value)) ||
                    (!multiple && option.value === value) ? (
                      <Icon
                        src="check_line"
                        color={theme.colors.cnam.blue.DEFAULT}
                        size={16}
                      />
                    ) : null}
                  </div>
                ))}
            </div>
          </div>
        )}
        {error && (
          <div className="flex items-center gap-1">
            <Icon
              src="information_fill"
              color={theme.colors.error.strong}
              size={16}
            />
            <span className="text-xs text-error">{errorMsg}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default Select;
